<template>
    <div class="platform-account">
        <div class="header">
            <div class="title">
                <IconFacebook />
                <span class="name">{{ platformAccount.name }}</span>
                <StatusPill
                    v-if="platformAccount.status === 'deleted'"
                    :status-label="platformAccount.status"
                    :status="StatusMap[platformAccount.status]"
                />
                <PublishStatusDetails :platform-account="platformAccount" :campaign-id="campaignId" />
            </div>

            <div class="actions">
                <div v-if="isDeleted">
                    <Tooltip placement="left" transfer>
                        <Button data-testid="connect__restore-account-button" @click="restorePlatformAccount">
                            <icon type="ios-undo" />
                            Restore account
                        </Button>
                        <template #content>
                            Restore the account
                            <br />
                            and all its rules.
                        </template>
                    </Tooltip>
                </div>

                <template v-else>
                    <Dropdown trigger="click">
                        <SquareButton data-testid="connect__settings-button">
                            <Icon type="ios-settings" />
                        </SquareButton>

                        <template #list>
                            <DropdownMenu>
                                <DropdownItem
                                    data-testid="connect__edit-account-button"
                                    @click="showEditAccountModal = true"
                                    >Edit</DropdownItem
                                >
                                <DropdownItem
                                    data-testid="connect__delete-account-button"
                                    @click="deletePlatformAccount"
                                    >Delete</DropdownItem
                                >
                            </DropdownMenu>
                        </template>
                    </Dropdown>

                    <SquareButton>
                        <Icon :key="index" type="ios-move" class="connect-accounts-drag-handle" />
                    </SquareButton>
                </template>

                <Button
                    v-if="platformStatus !== 'deleted'"
                    data-testid="connect__add-rule-button"
                    @click="showAddRuleModal = true"
                    >Add New Rule</Button
                >
            </div>
        </div>

        <hox-empty-message v-if="visibleRules.length === 0" class="empty" display-image-first type="no-content-icon">
            <template #title>
                <strong>You don't have any rules</strong>
            </template>
            <template #content>
                <p>Add a rule to create new signals for managing the timing and delivery of your campaigns.</p>
            </template>
        </hox-empty-message>

        <Draggable :model-value="visibleRules" item-key="element" handle=".rule-drag-handle" @update="updateRuleOrder">
            <template #item="{ element, index: ruleIndex }">
                <Rule
                    :platform-account="platformAccount"
                    :rule="element"
                    :rule-index="ruleIndex"
                    @showVariantsSelect="
                        emit('showVariantsSelect', {
                            platformAccountName: platformAccount.name,
                            ruleName: element.name
                        })
                    "
                />
            </template>
        </Draggable>

        <EditAccountModal
            v-if="showEditAccountModal"
            :platform-account="platformAccount"
            @close="showEditAccountModal = false"
        />

        <EditRuleModal
            v-if="showAddRuleModal"
            :account-index="index"
            :platform-account="platformAccount"
            @close="showAddRuleModal = false"
        />
    </div>
</template>

<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRoute } from "vue-router";
import Draggable from "vuedraggable";

import PublishStatusDetails from "@/Connect/components/Accounts/PlatformAccountPublishStatus.vue";
import EditAccountModal from "@/Connect/components/Accounts/PlatformAccountEdit.vue";
import EditRuleModal from "@/Connect/components/Accounts/PlatformAccountRuleEdit.vue";
import Rule from "@/Connect/components/Accounts/PlatformAccountRule.vue";
import StatusPill, { StatusMap } from "@/Connect/components/Accounts/StatusPill.vue";
import SquareButton from "@/components/common/SquareButton.vue";

import IconFacebook from "@/assets/intelligent-delivery/meta_social_media_icon.svg";
import type { Account } from "@/Connect/types";
import { useConnectStore } from "@/Connect/store";

const route = useRoute();
const connectStore = useConnectStore();

const props = defineProps<{
    account: Account;
    index: number;
}>();

const campaignId = route.params.campaignId as string;

const emit = defineEmits<(e: "showVariantsSelect", args: { platformAccountName: string; ruleName: string }) => void>();

/**
 * LOCAL STATE
 */
const showEditAccountModal = ref(false);
const showAddRuleModal = ref(false);

/**
 * COMPUTED
 */
const platformAccount = computed(() => props.account.platformAccount);
const isDeleted = computed(() => platformAccount.value.status === "deleted");
const platformStatus = computed(() => platformAccount.value.status);
const visibleRules = computed(() => {
    if (connectStore.showDeletedAccounts) {
        return props.account.rules;
    }

    return props.account.rules.filter(rule => rule.status !== "deleted");
});

/**
 * METHODS
 */
function updateRuleOrder({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) {
    connectStore.updateRuleOrder(platformAccount.value.name, oldIndex, newIndex);
}

function restorePlatformAccount() {
    connectStore.restoreDeletedPlatformAccount(platformAccount.value.name);
}

function deletePlatformAccount() {
    connectStore.deletePlatformAccount(platformAccount.value.name);
}
</script>

<style lang="scss" scoped>
.platform-account {
    margin-top: 40px;
    .header {
        display: flex;
        margin-bottom: 10px;
    }

    .title {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .name {
        font-size: 18px;
        font-weight: bold;
    }

    .actions {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 4px;

        i {
            cursor: pointer;
        }
    }

    .empty {
        border-radius: 8px;
        background-color: #ffffff;
        padding: 20px;
    }
}
</style>
